import { useState, useEffect } from "react";
import { apiFetch } from "../services/api.js";

/**
 * PersonaOwnerDashboard — restricted view for persona owners.
 * Shows only the single persona they manage, with quick stats and
 * navigation to persona management pages (via sidebar).
 */
export function PersonaOwnerDashboard({ persona, onNavigate }) {
  const [stats, setStats] = useState(null);
  const [kbCount, setKbCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const KB_MINIMUM = 10;
  const kbGateMet = kbCount >= KB_MINIMUM;

  useEffect(() => {
    if (!persona?.id) return;
    setLoading(true);
    Promise.all([
      apiFetch(`/api/candidates/${persona.id}/engagement`).catch(() => ({})),
      apiFetch(`/api/admin/personas/${persona.id}/kb-stats`).catch(() => ({ entryCount: 0 })),
    ]).then(([eng, kb]) => {
      setStats(eng);
      setKbCount(kb.entryCount ?? 0);
    }).finally(() => setLoading(false));
  }, [persona?.id]);

  if (!persona) {
    return (
      <div style={{ padding: "24px 28px", maxWidth: 700 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", fontFamily: "Georgia, serif" }}>Your AI Clone</h1>
        <div style={{ background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 12, padding: 32, textAlign: "center", marginTop: 20 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⏳</div>
          <h2 style={{ color: "#f1f5f9", fontSize: 18, fontWeight: 700, margin: "0 0 8px" }}>Application Under Review</h2>
          <p style={{ color: "#94a3b8", fontSize: 14, lineHeight: 1.6 }}>
            Your application is being reviewed by our team. You'll receive an email once it's approved.
          </p>
        </div>
      </div>
    );
  }

  const pc = persona.accent_color || persona.accentColor || "#3b82f6";

  return (
    <div style={{ padding: "24px 28px", maxWidth: 700 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#64748b", marginBottom: 6 }}>
          Your AI Clone
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", fontFamily: "Georgia, serif", margin: 0 }}>
          {persona.name}
        </h1>
        <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>{persona.role}</div>
      </div>

      {/* KB Gate Warning */}
      {!kbGateMet && (
        <div style={{
          padding: "14px 18px", marginBottom: 20, borderRadius: 12,
          background: "#422006", border: "1px solid #f59e0b40",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <span style={{ fontSize: 24 }}>⚠️</span>
          <div>
            <div style={{ color: "#f59e0b", fontSize: 14, fontWeight: 700, marginBottom: 2 }}>
              Knowledge Base Incomplete
            </div>
            <div style={{ color: "#fbbf24", fontSize: 13, lineHeight: 1.5 }}>
              Your clone needs at least {KB_MINIMUM} knowledge base entries before it can go live.
              Currently: <strong>{kbCount}/{KB_MINIMUM}</strong>.
              <button
                onClick={() => onNavigate?.("kb")}
                style={{ background: "none", border: "none", color: "#f59e0b", cursor: "pointer", padding: 0, fontFamily: "inherit", fontWeight: 700, textDecoration: "underline", marginLeft: 6 }}
              >
                Add entries →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status card */}
      <div style={{
        background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 12,
        padding: 20, marginBottom: 16,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em" }}>Clone Status</span>
          <span style={{
            padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700,
            background: persona.active ? "#064e3b" : "#422006",
            color: persona.active ? "#10b981" : "#f59e0b",
          }}>
            {persona.active ? (kbGateMet ? "Live" : "Pending KB") : "Inactive"}
          </span>
        </div>

        {/* Quick stats */}
        {loading ? (
          <div style={{ color: "#475569", fontSize: 13 }}>Loading stats...</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            <StatCard label="Citizens" value={stats?.uniqueVoters ?? 0} />
            <StatCard label="Sessions" value={stats?.totalSessions ?? 0} />
            <StatCard label="Questions" value={stats?.totalMessages ?? 0} />
          </div>
        )}
      </div>

      {/* KB progress */}
      <div style={{
        background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 12,
        padding: 20, marginBottom: 16,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em" }}>Knowledge Base</span>
          <span style={{ fontSize: 13, color: kbGateMet ? "#10b981" : "#f59e0b", fontWeight: 700 }}>
            {kbCount}/{KB_MINIMUM} entries
          </span>
        </div>
        <div style={{ height: 8, background: "#1e2d45", borderRadius: 4, overflow: "hidden" }}>
          <div style={{
            height: "100%", borderRadius: 4, transition: "width 0.3s",
            width: `${Math.min(100, (kbCount / KB_MINIMUM) * 100)}%`,
            background: kbGateMet ? "#10b981" : "#f59e0b",
          }} />
        </div>
        {kbGateMet && (
          <div style={{ color: "#10b981", fontSize: 12, marginTop: 6 }}>
            ✅ Minimum requirement met — your clone is ready to go live.
          </div>
        )}
      </div>

      {/* Quick actions */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <ActionCard icon="📚" label="Knowledge Base" desc="Add or edit training content" onClick={() => onNavigate?.("kb")} />
        <ActionCard icon="💬" label="Preview Chat" desc="Test your AI clone" onClick={() => onNavigate?.("chat")} />
        <ActionCard icon="📊" label="Analytics" desc="View engagement stats" onClick={() => onNavigate?.("analytics")} />
        <ActionCard icon="📋" label="Edit Profile" desc="Update public info" onClick={() => onNavigate?.("profile")} />
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{ textAlign: "center", padding: 12, background: "#090e1a", borderRadius: 10, border: "1px solid #1e2d4540" }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: "#f1f5f9" }}>{typeof value === "number" ? value.toLocaleString() : value}</div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 4 }}>{label}</div>
    </div>
  );
}

function ActionCard({ icon, label, desc, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 12, padding: "16px",
        background: "#0d1724", border: "1px solid #1e2d45", borderRadius: 12,
        cursor: "pointer", textAlign: "left", fontFamily: "inherit", width: "100%",
        transition: "border-color 0.15s",
      }}
    >
      <span style={{ fontSize: 24 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>{label}</div>
        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{desc}</div>
      </div>
    </button>
  );
}

export default PersonaOwnerDashboard;
